import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import { IncidentObjective } from '../../hooks/incidentObjectiveHooks';
import { GetIncidentActionResponse } from '../../../Actions/hooks/incidentActionHooks';
import { incidentObjectiveTypeSegments } from '../../incidentObjectiveTypes';

dayjs.extend(utc);

type Props = {
  objectives: IncidentObjective[];
  actions: GetIncidentActionResponse[];
};

type TimelineRow = {
  id: string;
  time: string | Date | null;
  kind: 'Objective' | 'Action';
  name: string;
  objectiveType?: string;
};

const objectiveTypeAbbrev = (type: string | undefined): string => {
  const match = incidentObjectiveTypeSegments.find((s) => s.value === type);
  return match?.abbrev ?? 'O';
};

const formatDate = (value: string | Date | undefined | null) => {
  if (!value) return '';
  const d = dayjs.utc(value).local();
  return d.isValid() ? d.format('MM/DD/YYYY HH:mm') : '';
};

const toTime = (value: string | Date | undefined | null): number => {
  if (!value) return Number.POSITIVE_INFINITY;
  const t = new Date(value as string).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};

const TimelineTable: React.FC<Props> = ({ objectives, actions }) => {
  const rows = useMemo<TimelineRow[]>(() => {
    const objectiveRows: TimelineRow[] = objectives
      .filter((o) => !o.archived)
      .map((o) => ({
        id: `O:${o.objective_id}`,
        time: o.timestamp,
        kind: 'Objective',
        name: o.name,
        objectiveType: o.type,
      }));
    const actionRows: TimelineRow[] = actions
      .filter((a) => !a.archived)
      .map((a) => ({
        id: `A:${a.id}`,
        time: a.timestamp,
        kind: 'Action',
        name: a.name,
      }));
    return [...objectiveRows, ...actionRows].sort(
      (a, b) => toTime(a.time) - toTime(b.time)
    );
  }, [objectives, actions]);

  const columnHelper = createColumnHelper<TimelineRow>();

  const columns = useMemo<ColumnDef<TimelineRow, any>[]>(
    () => [
      columnHelper.accessor((row) => row.time, {
        id: 'time',
        header: () => <span>Time</span>,
        enableSorting: true,
        sortingFn: 'datetime',
        cell: (info) => <span>{formatDate(info.getValue())}</span>,
      }),
      columnHelper.accessor('name', {
        id: 'label',
        header: () => <span>Item</span>,
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          const label =
            row.kind === 'Objective'
              ? `Objective (${objectiveTypeAbbrev(row.objectiveType)})`
              : row.kind;
          return (
            <span>
              {label}: {row.name}
            </span>
          );
        },
        meta: { align: 'left' },
      }),
    ],
    [columnHelper]
  );

  return (
    <PratusTable
      className="pratus-table"
      columns={columns}
      data={rows}
      getRowId={(row) => row.id}
      responsive={false}
      noDataIndication={
        <div className="text-muted p-3">No objectives or actions yet</div>
      }
    />
  );
};

export default TimelineTable;
